import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { SynthesizeSpeechWithTimingsDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { AVAILABLE_FONTS } from "@ai-teach/canvas-styles";
import { MediaUsageContextSchema, type TextChoiceElement, type TextElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaFont,
  FaItalic,
  FaMinus,
  FaPlus,
  FaSpinner,
  FaStrikethrough,
  FaSyncAlt,
  FaUnderline,
  FaVolumeUp,
  FaWaveSquare,
} from "react-icons/fa";
import { MdFormatColorText } from "react-icons/md";
import { BorderSettings } from "./BorderSettings";

// A tiny silent WAV. Played inside the user's click to unlock the shared <audio> element so the real
// playback (fired after the async TTS request) isn't blocked by the browser autoplay policy.
const SILENT_AUDIO_SRC =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

interface TextElementToolbarProps {
  textElement: TextElement | TextChoiceElement;
}

export function TextElementToolbar({ textElement }: TextElementToolbarProps) {
  const dispatch = useAppDispatch();
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);
  const [localTextColor, setLocalTextColor] = useState(textElement?.style?.color || "#000000");
  const [localBgColor, setLocalBgColor] = useState(textElement.backgroundColor ?? "#ffffff");
  const [localFontSize, setLocalFontSize] = useState(String(textElement?.style?.fontSize || 16));
  const textColorTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const bgColorTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const [synthesizeSpeech, { loading: isGeneratingTts }] = useMutation(
    SynthesizeSpeechWithTimingsDocument
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);

  useEffect(() => {
    setLocalTextColor(textElement?.style?.color || "#000000");
  }, [textElement?.style?.color]);

  useEffect(() => {
    setLocalBgColor(textElement.backgroundColor ?? "#ffffff");
  }, [textElement.backgroundColor]);

  useEffect(() => {
    setLocalFontSize(String(textElement?.style?.fontSize || 16));
  }, [textElement?.style?.fontSize]);

  if (!slide) return null;

  const selectedElement = slide.elements.find((el) => el.id === selectedId);

  if (!selectedElement) {
    return null;
  }

  const handleTextColorChange = (color: string) => {
    setLocalTextColor(color);
    if (textColorTimer.current) clearTimeout(textColorTimer.current);
    textColorTimer.current = setTimeout(() => {
      if (selectedId && textElement) {
        dispatch(
          updateElementProps({
            id: selectedId,
            props: { style: { ...textElement.style, color } },
          })
        );
      }
    }, 80);
  };

  const handleFontFamilyChange = (fontFamily: string) => {
    if (selectedId && textElement) {
      dispatch(
        updateElementProps({
          id: selectedId,
          props: { style: { ...textElement.style, fontFamily } },
        })
      );
    }
  };

  const handleFontSizeChange = (delta: number) => {
    if (selectedId && textElement) {
      const newSize = (textElement.style.fontSize || 16) + delta;
      const clampedSize = Math.max(8, newSize);
      dispatch(
        updateElementProps({
          id: selectedId,
          props: {
            style: { ...textElement.style, fontSize: clampedSize },
          },
        })
      );
      setLocalFontSize(String(clampedSize));
    }
  };

  const handleFontSizeInput = (value: string) => {
    setLocalFontSize(value);
  };

  const handleFontSizeBlur = () => {
    if (selectedId && textElement) {
      const parsedSize = Number.parseInt(localFontSize, 10);
      const newSize = Number.isNaN(parsedSize) ? 16 : Math.max(8, parsedSize);
      dispatch(
        updateElementProps({
          id: selectedId,
          props: {
            style: { ...textElement.style, fontSize: newSize },
          },
        })
      );
      setLocalFontSize(String(newSize));
    }
  };

  const handleFontSizeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const toggleBold = () => {
    if (selectedId && textElement) {
      const isBold =
        textElement.style.fontWeight === "bold" || textElement.style.fontWeight === "700";
      dispatch(
        updateElementProps({
          id: selectedId,
          props: {
            style: {
              ...textElement.style,
              fontWeight: isBold ? "normal" : "bold",
            },
          },
        })
      );
    }
  };

  const handleTextAlign = (align: "left" | "center" | "right") => {
    if (selectedId && textElement) {
      dispatch(
        updateElementProps({
          id: selectedId,
          props: { style: { ...textElement.style, textAlign: align } },
        })
      );
    }
  };

  const toggleTextDecoration = (decorationType: "underline" | "line-through") => {
    const currentDecoration = textElement.style.textDecoration || "none";
    const decorations = currentDecoration.split(" ").filter((d) => d !== "none");

    const hasDecoration = decorations.includes(decorationType);

    const newDecorations = hasDecoration
      ? decorations.filter((d) => d !== decorationType)
      : [...decorations, decorationType];

    const newDecoration = newDecorations.length > 0 ? newDecorations.join(" ") : "none";

    if (selectedId && textElement) {
      dispatch(
        updateElementProps({
          id: selectedId,
          props: {
            style: { ...textElement.style, textDecoration: newDecoration },
          },
        })
      );
    }
  };

  const handleStrikeThrough = () => toggleTextDecoration("line-through");
  const handleUnderline = () => toggleTextDecoration("underline");

  const handleItalic = () => {
    if (selectedId && textElement) {
      const isItalic = textElement.style.fontStyle === "italic";

      dispatch(
        updateElementProps({
          id: selectedId,
          props: {
            style: {
              ...textElement.style,
              fontStyle: isItalic ? "normal" : "italic",
            },
          },
        })
      );
    }
  };

  const handleBorderLineStyle = (lineStyle: string) => {
    dispatch(
      updateElementProps({
        id: textElement.id,
        props: { border: { ...textElement.border, lineStyle } },
      })
    );
  };

  const handleBorderCornerRadius = (cornerRadius: number) => {
    dispatch(
      updateElementProps({
        id: textElement.id,
        props: { border: { ...textElement.border, cornerRadius } },
      })
    );
  };

  const handleBorderWidth = (strokeWidth: number) => {
    dispatch(
      updateElementProps({
        id: textElement.id,
        props: { border: { ...textElement.border, strokeWidth } },
      })
    );
  };

  const handleBorderColorChange = (stroke: string) => {
    dispatch(
      updateElementProps({
        id: textElement.id,
        props: { border: { ...textElement.border, stroke } },
      })
    );
  };

  const handleBackgroundColorChange = (color: string) => {
    setLocalBgColor(color);
    if (bgColorTimer.current) clearTimeout(bgColorTimer.current);
    bgColorTimer.current = setTimeout(() => {
      dispatch(
        updateElementProps({
          id: textElement.id,
          props: { backgroundColor: color },
        })
      );
    }, 80);
  };

  // A single reusable audio element. Reusing one element (rather than `new Audio()` per call) lets us
  // "unlock" it inside a user gesture so a later programmatic play() — after the async TTS request —
  // isn't blocked by the browser autoplay policy.
  const getAudioEl = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  const playSrc = (src: string) => {
    const audio = getAudioEl();
    audio.src = src;
    audio.play().catch((err) => {
      console.error("[TextElementTTS] audio playback failed", err);
      toast.error("Couldn't play audio — click the speaker again", { id: "tts-play-error" });
    });
  };

  // Generate speech (with per-word timings), persist the S3 reference + timings onto the element, play.
  const generateAndPlay = () => {
    if (textElement.type !== "text") return;

    // Unlock the audio element within the current click gesture. A silent WAV lets play() resolve now
    // so the real playback after the awaited mutation is treated as user-initiated. Fire-and-forget.
    const audio = getAudioEl();
    audio.src = SILENT_AUDIO_SRC;
    audio.play().catch(() => {});

    const text = textElement.content;
    if (!text?.trim()) {
      toast.error("No text to read aloud", { id: "tts-empty-error" });
      return;
    }

    withToast(
      async () => {
        const res = await synthesizeSpeech({
          variables: {
            text,
            voice: DEFAULT_VOICE,
            speed: 1,
            pitch: 0,
          },
        });

        const generated = res.data?.synthesizeSpeechWithTimings;
        if (!generated?.src) throw new Error();

        dispatch(
          updateElementProps({
            id: textElement.id,
            props: {
              audio: {
                src: generated.src,
                filename: generated.filename ?? null,
                id: generated.id ?? null,
              },
              audioWordBoundaries:
                generated.wordBoundaries?.map((w) => ({
                  text: w.text,
                  offset: w.offset,
                  duration: w.duration,
                })) ?? undefined,
            },
          })
        );

        playSrc(generated.src);
      },
      {
        loading: "Generating audio...",
        success: "Audio ready",
        error: "Failed to generate audio",
      },
      "textElementTtsToast"
    );
  };

  const toggleAudioDisplayMode = () => {
    if (textElement.type !== "text") return;
    const next = textElement.audioDisplayMode === "player" ? "text" : "player";
    dispatch(updateElementProps({ id: textElement.id, props: { audioDisplayMode: next } }));
  };

  // Play the stored audio if we already have it, otherwise generate it first.
  const handleSpeak = () => {
    if (textElement.type === "text" && textElement.audio?.src) {
      playSrc(textElement.audio.src);
      return;
    }
    generateAndPlay();
  };

  const hasAudio = textElement.type === "text" && !!textElement.audio?.src;

  const isMediaType = ["audioPlayer", "image", "video"].includes(selectedElement.type);

  if (isMediaType) return;

  return (
    <>
      {/* Font Family */}
      <select
        value={textElement?.style?.fontFamily || "System"}
        onChange={(e) => handleFontFamilyChange(e.target.value)}
        className="h-8 px-2.5 py-0 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer hover:border-primary-300"
      >
        {AVAILABLE_FONTS.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>

      {/* Font Size Controls */}
      <div className="mx-2 h-8 flex items-center border rounded-lg border-gray-300 bg-white/50 overflow-hidden">
        <button
          type="button"
          onClick={() => handleFontSizeChange(-1)}
          aria-label="Decrease Font Size"
          className="flex h-8 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <FaMinus className="w-3 h-3" />
        </button>
        <input
          type="number"
          value={localFontSize}
          onChange={(e) => handleFontSizeInput(e.target.value)}
          onBlur={handleFontSizeBlur}
          onKeyDown={handleFontSizeKeyDown}
          min="8"
          className="h-8 w-10 text-center text-sm font-medium leading-none text-gray-700 bg-transparent border-x border-gray-200 focus:outline-none focus:ring-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          aria-label="Font Size"
        />
        <button
          type="button"
          onClick={() => handleFontSizeChange(1)}
          aria-label="Increase Font Size"
          className="flex h-8 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      {/* Text Color */}
      <div className="flex flex-col items-center relative group">
        <label
          htmlFor="text-color"
          className="cursor-pointer p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center"
        >
          <MdFormatColorText fontSize={20} />
          <div
            className="w-4 h-1 rounded-full mt-0.5 ring-1 ring-gray-300"
            style={{ backgroundColor: localTextColor }}
          />
        </label>
        <input
          type="color"
          id="text-color"
          value={localTextColor}
          onChange={(e) => handleTextColorChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          title="Text Color"
        />
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Text Border */}
      <BorderSettings
        border={textElement.border}
        onLineStyleChange={handleBorderLineStyle}
        onStrokeWidthChange={handleBorderWidth}
        onCornerRadiusChange={handleBorderCornerRadius}
        onColorChange={handleBorderColorChange}
      />

      {/* Background color */}
      <label className="cursor-pointer">
        <div className="relative group">
          <div
            className="w-8 h-8 rounded-lg border-2 border-gray-300 transition-all duration-200 group-hover:border-primary-400 group-hover:scale-105 group-hover:shadow-md"
            style={{ backgroundColor: localBgColor }}
          />
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400" />
          </div>
        </div>
        <input
          type="color"
          value={localBgColor}
          onChange={(e) => handleBackgroundColorChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          title="Border Color"
        />
      </label>

      <div className="w-px h-6 bg-gray-300" />

      {/* Bold */}
      <IconButton
        onClick={toggleBold}
        variant="ghost"
        size="sm"
        aria-label="Bold"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.fontWeight === "bold" || textElement?.style?.fontWeight === "700"
            ? "bg-primary-100 text-primary-600 ring-1 ring-primary-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaBold className="w-4 h-4" />
      </IconButton>

      {/* Italic */}
      <IconButton
        onClick={handleItalic}
        variant="ghost"
        size="sm"
        aria-label="Italic"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.fontStyle === "italic"
            ? "bg-accent-100 text-accent-600 ring-1 ring-accent-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaItalic className="w-4 h-4" />
      </IconButton>

      {/* Underline */}
      <IconButton
        onClick={handleUnderline}
        variant="ghost"
        size="sm"
        aria-label="Underline"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.textDecoration?.includes("underline")
            ? "bg-blue-100 text-blue-600 ring-1 ring-blue-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaUnderline className="w-4 h-4" />
      </IconButton>

      {/* Strikethrough */}
      <IconButton
        onClick={handleStrikeThrough}
        variant="ghost"
        size="sm"
        aria-label="Strikethrough"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.textDecoration?.includes("line-through")
            ? "bg-rose-100 text-rose-600 ring-1 ring-rose-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaStrikethrough className="w-4 h-4" />
      </IconButton>

      <div className="w-px h-6 bg-gray-300" />

      {/* Text Alignment */}
      <IconButton
        onClick={() => handleTextAlign("left")}
        variant="ghost"
        size="sm"
        aria-label="Align Left"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.textAlign === "left"
            ? "bg-emerald-100 text-emerald-600 ring-1 ring-emerald-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaAlignLeft className="w-4 h-4" />
      </IconButton>
      <IconButton
        onClick={() => handleTextAlign("center")}
        variant="ghost"
        size="sm"
        aria-label="Align Center"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.textAlign === "center"
            ? "bg-emerald-100 text-emerald-600 ring-1 ring-emerald-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaAlignCenter className="w-4 h-4" />
      </IconButton>
      <IconButton
        onClick={() => handleTextAlign("right")}
        variant="ghost"
        size="sm"
        aria-label="Align Right"
        className={`p-2 rounded-lg transition-all duration-200 ${
          textElement?.style?.textAlign === "right"
            ? "bg-emerald-100 text-emerald-600 ring-1 ring-emerald-300"
            : "hover:bg-gray-100 hover:text-black"
        }`}
      >
        <FaAlignRight className="w-4 h-4" />
      </IconButton>

      {/* Text-to-Speech: read aloud + regenerate (text elements only) */}
      {textElement.type === "text" && (
        <>
          <div className="w-px h-6 bg-gray-300" />

          {/* Read aloud (plays stored audio, or generates it on first use) */}
          <IconButton
            onClick={handleSpeak}
            disabled={isGeneratingTts}
            variant="ghost"
            size="sm"
            aria-label={hasAudio ? "Play audio" : "Generate audio"}
            title={hasAudio ? "Play audio 播放語音" : "Generate audio 產生音檔"}
            className="p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 hover:text-black disabled:opacity-50"
          >
            {isGeneratingTts ? (
              <FaSpinner className="w-4 h-4 animate-spin" />
            ) : (
              <FaVolumeUp className="w-4 h-4" />
            )}
          </IconButton>

          {/* Regenerate audio (only when audio exists, e.g. after editing the text) */}
          {hasAudio && (
            <IconButton
              onClick={() => setIsVoiceSettingsOpen(true)}
              disabled={isGeneratingTts}
              variant="ghost"
              size="sm"
              aria-label="Regenerate audio"
              title="Regenerate audio 重新產生音檔"
              className="p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 hover:text-black disabled:opacity-50"
            >
              <FaSyncAlt className="w-3.5 h-3.5" />
            </IconButton>
          )}

          {/* Display mode: toggle between showing the text and showing a compact player */}
          {hasAudio && (
            <IconButton
              onClick={toggleAudioDisplayMode}
              variant="ghost"
              size="sm"
              aria-label={
                textElement.audioDisplayMode === "player" ? "Show text" : "Show player only"
              }
              title={
                textElement.audioDisplayMode === "player"
                  ? "Switch to text mode 切換為文字模式"
                  : "Switch to player mode 切換為播放器模式"
              }
              className={`p-2 rounded-lg transition-all duration-200 ${
                textElement.audioDisplayMode === "player"
                  ? "bg-primary-100 text-primary-600 ring-1 ring-primary-300"
                  : "hover:bg-gray-100 hover:text-black"
              }`}
            >
              {textElement.audioDisplayMode === "player" ? (
                <FaFont className="w-3.5 h-3.5" />
              ) : (
                <FaWaveSquare className="w-3.5 h-3.5" />
              )}
            </IconButton>
          )}
        </>
      )}

      {isVoiceSettingsOpen && slide && textElement.type === "text" && (
        <VoiceSettingsModal
          audioData={{
            id: textElement.audio?.id ?? null,
            filename: textElement.audio?.filename ?? null,
            src: textElement.audio?.src ?? null,
          }}
          onClose={() => setIsVoiceSettingsOpen(false)}
          text={textElement.content ?? null}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename, wordBoundaries }) => {
            dispatch(
              updateElementProps({
                id: textElement.id,
                props: {
                  audio: { src, filename, id },
                  audioWordBoundaries:
                    wordBoundaries?.map((w) => ({
                      text: w.text,
                      offset: w.offset,
                      duration: w.duration,
                    })) ?? undefined,
                },
              })
            );
            setIsVoiceSettingsOpen(false);
          }}
        />
      )}
    </>
  );
}
